import { useMemo } from 'react';
import { useDataProvider } from '@refinedev/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BaseRecord, CrudFilter, CrudSort } from '@refinedev/core';

/**
 * useAllPages — универсальная парционная загрузка всего списка.
 *
 * Делает первый запрос `?page=1&limit=pageSize`, читает `total` из meta,
 * затем параллельно догружает остальные страницы и склеивает результат.
 * Кэшируется через TanStack Query (общий QueryClient, ключ зависит от
 * resource + filters + sorters + pageSize).
 *
 * Назначение — заменить `useList({ pagination: { mode: 'off' } })` и
 * `useList({ pagination: { pageSize: 200 } })` в admin-формах, где
 * нужен полный список (multi-select, lookup-grid и т.д.) — без риска
 * упереться в backend `limit ≤ 100` и без жёсткого capа на UI-стороне.
 *
 * Подход — offset-based (page/limit), потому что backend уже так умеет
 * у всех list-эндпойнтов. Keyset-cursor не нужен: для админ-форм
 * каталоги небольшие (десятки-сотни записей), а параллельная дозагрузка
 * страниц укладывается в <500ms даже для 1000 записей.
 *
 * @example
 *   const { data, isLoading } = useAllPages<Course>({ resource: 'courses' });
 *   // data: Course[]  — все курсы
 *
 *   const { data } = useAllPages<Book>({
 *     resource: 'books',
 *     filters: [{ field: 'isActive', operator: 'eq', value: true }],
 *     enabled: dialogOpen,
 *   });
 */

const DEFAULT_PAGE_SIZE = 100;
const MAX_PAGES_HARD_CAP = 50; // 50 * 100 = 5000 записей. Защита от бесконечного цикла.

export interface UseAllPagesOptions {
  /** Resource name как в Refine (соответствует endpoint в data-provider). */
  resource: string;
  /** Refine-формат фильтров. Передаются в dataProvider.getList как есть. */
  filters?: CrudFilter[];
  /** Refine-формат сортировки. */
  sorters?: CrudSort[];
  /**
   * Размер одной страницы при дозагрузке. По умолчанию 100 — максимум,
   * который безопасно принимают все backend-эндпойнты.
   */
  pageSize?: number;
  /**
   * Если false — запрос не делается (для условной загрузки).
   * Аналог Refine queryOptions.enabled.
   */
  enabled?: boolean;
  /**
   * Свежесть данных в мс. Дефолт 60 секунд — для админ-каталогов комфортно.
   */
  staleTime?: number;
}

export interface UseAllPagesResult<T> {
  data: T[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** Общее количество записей (длина склеенного массива). */
  total: number;
  /** Принудительно перечитать все страницы. */
  refetch: () => Promise<void>;
}

interface GetListResponse<T> {
  data: T[];
  total: number;
}

export function useAllPages<T extends BaseRecord = BaseRecord>(
  options: UseAllPagesOptions,
): UseAllPagesResult<T> {
  const {
    resource,
    filters,
    sorters,
    pageSize = DEFAULT_PAGE_SIZE,
    enabled = true,
    staleTime = 60_000,
  } = options;

  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();

  // queryKey стабилизируем через JSON-стрингификацию — filters/sorters
  // могут быть новыми объектами при каждом рендере parent-компонента.
  const queryKey = useMemo(
    () => [
      'all-pages',
      resource,
      pageSize,
      JSON.stringify(filters ?? []),
      JSON.stringify(sorters ?? []),
    ] as const,
    [resource, pageSize, filters, sorters],
  );

  const query = useQuery<T[]>({
    queryKey,
    enabled,
    staleTime,
    queryFn: async () => {
      const provider = dataProvider();
      if (typeof provider.getList !== 'function') {
        throw new Error(`useAllPages: data provider has no getList for resource "${resource}"`);
      }

      // Первая страница — чтобы узнать total и взять первые pageSize записей.
      const first = (await provider.getList({
        resource,
        pagination: { currentPage: 1, pageSize, mode: 'server' },
        filters,
        sorters,
      })) as GetListResponse<T>;

      const total = first.total ?? first.data.length;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));

      if (totalPages <= 1) {
        return first.data;
      }

      // Защита: если backend вдруг отдал бредовый total, не лезем в бесконечный цикл.
      const effectivePages = Math.min(totalPages, MAX_PAGES_HARD_CAP);

      // Параллельно догружаем страницы 2..N. Order сохраняется через массив.
      const restPromises: Promise<GetListResponse<T>>[] = [];
      for (let page = 2; page <= effectivePages; page++) {
        restPromises.push(
          provider.getList({
            resource,
            pagination: { currentPage: page, pageSize, mode: 'server' },
            filters,
            sorters,
          }) as Promise<GetListResponse<T>>,
        );
      }

      const restResults = await Promise.all(restPromises);
      const merged: T[] = [...first.data];
      for (const r of restResults) {
        merged.push(...r.data);
      }
      return merged;
    },
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    total: query.data?.length ?? 0,
    refetch: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  };
}
